import { ConfirmSheet } from '../Sheets';
import React, { useState } from 'react';
import { displayName, nameInitials } from '../../../services/displayName';
import { colors } from '../../../theme';
import { S } from '../dashboardStyles';
import EmptyState from '../EmptyState';
import { Phone, MessageSquare, Search, UserPlus, User, Check, X, MoreVertical, Trash2, Ban, Clock } from 'lucide-react';

// ADR-013: contacts = accepted friends only. No global directory, no freeform
// address book. Add people via exact-match search + friend request.
export default function ContactsTab({
  users, user, userStatuses,
  contactSearch, setContactSearch,
  searchResults, searchBusy, onSearch, sendFriendRequest,
  incomingRequests, acceptRequest, declineRequest,
  outgoingRequests = [], cancelRequest,
  blockedUsers = [], unblockUser,
  removeFriend, blockUser,
  callUser, openChat, setTab,
  formatLastSeen, utc,
}) {
  const [menuFor, setMenuFor] = useState(null);
  // Removing a contact and blocking one both used to be four words over a
  // modal. Both are worth a sentence about what happens afterwards.
  const [confirm, setConfirm] = useState(null);
  const q = contactSearch.trim();

  // The search box asks the server for an EXACT match, which is how you add a
  // stranger (ADR-013 keeps the directory closed). It is not how you find
  // someone you already have: typing part of a contact's name used to return
  // "никого не найдено" while that very person sat in the list below. Your own
  // contacts are filtered here, on the device, with no round trip.
  const ql = q.toLowerCase();
  const shown = ql
    ? users.filter(u => displayName(u).toLowerCase().includes(ql) || (u.username || '').toLowerCase().includes(ql))
    : users;

  return (
    <div>
      <form
        style={S.searchWrap}
        onSubmit={(e) => { e.preventDefault(); onSearch(contactSearch); }}
      >
        <span style={S.searchIcon}><Search size={16} color={colors.gray400} /></span>
        <input
          style={S.searchInput}
          value={contactSearch}
          onChange={e => { setContactSearch(e.target.value); onSearch(e.target.value); }}
          placeholder="Найти по точному имени или номеру..."
        />
      </form>

      {/* ─── Search results (exact match) ─── */}
      {q.length > 0 && (
        <>
          <div style={S.sectionLabel}>Поиск</div>
          {searchBusy && <div style={{ ...S.compactSub, padding: '8px 4px' }}>Поиск...</div>}
          {!searchBusy && searchResults.length === 0 && (
            <EmptyState icon={Search} text="Никого не найдено" sub="Нужно точное имя пользователя или номер" />
          )}
          {!searchBusy && searchResults.map(r => (
            <div key={r.id} style={S.compactRow}>
              <div style={S.compactAvatar}>{nameInitials(r)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.compactName}>{displayName(r)}</div>
                <div style={S.compactSub}>
                  {r.relationship === 'friends' ? 'Уже в контактах'
                    : r.relationship === 'requested' ? 'Запрос отправлен'
                    : r.relationship === 'incoming' ? 'Ждёт вашего ответа'
                    : 'Не в контактах'}
                </div>
              </div>
              {(r.relationship === 'none' || r.relationship === 'incoming') && (
                <button onClick={() => sendFriendRequest(r.id)} style={S.compactIconBtn} title="Добавить в контакты">
                  <UserPlus size={16} color={colors.gray600} />
                </button>
              )}
            </div>
          ))}
        </>
      )}

      {/* ─── Incoming friend requests ─── */}
      {incomingRequests.length > 0 && (
        <>
          <div style={{ ...S.sectionLabel, marginTop: q ? 20 : 0 }}>Заявки в контакты</div>
          {incomingRequests.map(rq => (
            <div key={rq.id} style={S.compactRow}>
              <div style={S.compactAvatar}>{nameInitials(rq)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.compactName}>{displayName(rq)}</div>
                <div style={S.compactSub}>хочет добавить вас</div>
              </div>
              <button onClick={() => acceptRequest(rq.user_id)} style={{ ...S.compactIconBtn, color: colors.green }} title="Принять">
                <Check size={18} color={colors.green} />
              </button>
              <button onClick={() => declineRequest(rq.user_id)} style={S.compactIconBtn} title="Отклонить">
                <X size={18} color={colors.gray500} />
              </button>
            </div>
          ))}
        </>
      )}

      {/* ─── Requests I sent ─── */}
      {outgoingRequests.length > 0 && (
        <>
          <div style={{ ...S.sectionLabel, marginTop: (q || incomingRequests.length) ? 20 : 0 }}>Отправленные заявки</div>
          {outgoingRequests.map(rq => (
            <div key={rq.id} style={S.compactRow}>
              <div style={S.compactAvatar}>{nameInitials(rq)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.compactName}>{displayName(rq)}</div>
                <div style={{ ...S.compactSub, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={12} color={colors.gray500} /> ждёт ответа
                </div>
              </div>
              <button onClick={() => cancelRequest(rq.user_id)} style={S.compactIconBtn} title="Отменить заявку">
                <X size={18} color={colors.gray500} />
              </button>
            </div>
          ))}
        </>
      )}

      {/* ─── Friends (the only directory) ─── */}
      {shown.length > 0 && <div style={{ ...S.sectionLabel, marginTop: (q || incomingRequests.length || outgoingRequests.length) ? 20 : 0 }}>Контакты</div>}
      {shown.map(u => {
        const rtStatus = userStatuses[u.id];
        // `online` comes from the server's live connection map. last_seen only
        // records the last login, so it used to show a green dot for someone who
        // had merely signed in once and closed the app.
        // `u.online` is a REST snapshot taken when this screen loaded; rtStatus
        // is live. An explicit 'offline' used to match none of the branches
        // above and fall through to the snapshot, so the dot stayed green for
        // someone who had since disconnected — while the call button, which
        // reads rtStatus, refused with "абонент не в сети". Two sources for one
        // fact, disagreeing on screen. The snapshot is now only consulted when
        // there is no live answer at all.
        const dotColor = rtStatus === 'app_full' ? colors.green
          : rtStatus === 'app_no_sip' ? colors.amber
          // Asleep: the app is closed and a push wakes it. Not the same as
          // offline — a call to this person goes through — and not the same as
          // online either, so it gets its own, quieter dot.
          : rtStatus === 'asleep' ? '#8a8f98'
          : rtStatus === 'external_sip' ? '#6e8ad0'
          : rtStatus === 'offline' ? null
          : u.online ? colors.green : null;
        return (
          <div key={u.id} style={{ ...S.compactRow, cursor: 'pointer' }} onClick={() => openChat(u)}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={S.compactAvatar}>{nameInitials(u)}</div>
              {dotColor && <div style={{ ...S.onlineDot, background: dotColor }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.compactName}>{displayName(u)}</div>
              <div style={S.compactSub}>{formatLastSeen(u.last_seen) || 'офлайн'}</div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); callUser(u.sip_extension, u.id); }} style={S.compactIconBtn} title="Позвонить"><Phone size={16} color={colors.gray600} /></button>
            <button onClick={(e) => { e.stopPropagation(); setTab('chat'); setTimeout(() => openChat(u), 100); }} style={S.compactIconBtn} title="Написать"><MessageSquare size={16} color={colors.gray600} /></button>
            <button onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === u.id ? null : u.id); }} style={S.compactIconBtn} title="Ещё">
              <MoreVertical size={16} color={colors.gray600} />
            </button>
          </div>
        );
      })}

      {/* The actions the app has been able to perform all along, with nothing
          on screen to reach them: removeFriend and blockUser were written,
          wired to nothing, and never called once. */}
      {menuFor && (
        <div
          onClick={() => setMenuFor(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', background: colors.white, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: '8px 0 24px' }}>
            <div style={{ ...S.sectionLabel, padding: '12px 20px 4px' }}>
              {displayName(users.find(u => u.id === menuFor) || {})}
            </div>
            <button
              onClick={() => { const id = menuFor; setMenuFor(null); setConfirm({ kind: 'remove', id }); }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: colors.gray900, fontFamily: 'inherit' }}
            >
              <Trash2 size={18} color={colors.gray600} /> Удалить контакт
            </button>
            <button
              onClick={() => { const id = menuFor; setMenuFor(null); setConfirm({ kind: 'block', id }); }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: colors.red, fontFamily: 'inherit' }}
            >
              <Ban size={18} color={colors.red} /> Заблокировать
            </button>
          </div>
        </div>
      )}

      {/* ─── Blocked ─── */}
      {blockedUsers.length > 0 && !q && (
        <>
          <div style={{ ...S.sectionLabel, marginTop: 20 }}>Заблокированные</div>
          {blockedUsers.map(b => (
            <div key={b.id} style={S.compactRow}>
              <div style={{ ...S.compactAvatar, opacity: 0.5 }}>{nameInitials(b)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...S.compactName, color: colors.gray500 }}>{displayName(b)}</div>
                <div style={S.compactSub}>не может звонить и писать</div>
              </div>
              <button onClick={() => unblockUser(b.id)} style={{ ...S.compactIconBtn, width: 'auto', padding: '6px 12px', fontSize: 13, color: colors.gray700 }}>
                Разблокировать
              </button>
            </div>
          ))}
        </>
      )}

      {q && shown.length === 0 && users.length > 0 && searchResults.length === 0 && !searchBusy && (
        <EmptyState icon={Search} text="Среди ваших контактов совпадений нет" sub="Точное имя или номер ищет человека вне списка" />
      )}

      {users.length === 0 && incomingRequests.length === 0 && outgoingRequests.length === 0 && !q && (
        <EmptyState icon={User} text="Нет контактов" sub="Найдите человека по имени или номеру и отправьте заявку" />
      )}
      {confirm && (
        <ConfirmSheet
          title={confirm.kind === 'remove' ? 'Удалить контакт?' : 'Заблокировать человека?'}
          consequences={confirm.kind === 'remove'
            ? ['Переписка останется на этом устройстве, но написать друг другу вы больше не сможете',
               'Человек не увидит, что его удалили — для него вы просто перестанете быть доступны',
               'Чтобы вернуть контакт, понадобится новый запрос — от вас или от него']
            : ['Он не сможет вам звонить и писать',
               'Он не увидит, в сети вы или нет',
               'Снять блокировку можно здесь же, в списке контактов']}
          confirmLabel={confirm.kind === 'remove' ? 'Удалить контакт' : 'Заблокировать'}
          onConfirm={() => {
            const { kind, id } = confirm;
            setConfirm(null);
            if (kind === 'remove') removeFriend(id); else blockUser(id);
          }}
          onClose={() => setConfirm(null)}
        />
      )}

    </div>
  );
}
